import { Directive, Input, QueryList, TemplateRef } from '@angular/core';

/**
 * interfaces
 */
import { ITemplateConfig, ITemplateRef } from './interfaces/template';

/**
 * @directive TemplateDirective
 * @selector [appTemplate]
 * @description
 * A reusable structural directive to associate a `TemplateRef` with an optional name and context object.
 * Commonly used to allow flexible injection of templates into component views (e.g. timelines, cards, modals).
 *
 * @example
 * <ng-template appTemplate="header">Header Content</ng-template>
 * <ng-template [appTemplate]="{ tempName: 'header', tempContext: { title: 'Hello' } }">Custom</ng-template>
 */
@Directive({
  selector: '[appTemplate]',
})
export class TemplateDirective {
  
  /**
   * @property templateConfig
   * @input appTemplate
   * Accepts either a string (template name) or an object with name and context.
   */
  @Input('appTemplate')
  public set templateConfig(value: string | ITemplateConfig<any>) {

    // void
    if (value == null) return;

    if (typeof value === 'string') {
      // pass name without context
      this.name = value;
    }
    else if (typeof value === 'object') {
      // name with context
      this.name = value.tempName;
      this.context = value.tempContext;
    }

  }

  /**
   * @property id
   * @input templateId
   * Optional identifier for the template.
   */
  @Input({ alias: 'templateId' })
  public id!: string | number

  /**
   * @property context
   * @input templateContext
   * Optional context object available inside the template.
   */
  @Input({ alias: 'templateContext' })
  public context: any = {};

  /**
   * @property name
   * Internal name used for identifying the template.
   */
  public name: string = "";
  

  constructor(public templateRef: TemplateRef<any>) {}

  /**
   * @method templateRefWithContext
   * @description Returns the current template along with its context.
   */
  public templateRefWithContext<T>(): ITemplateRef<T> {
    return { tempRef: this.templateRef, tempContext: this.context };
  }  

  /**
   * @description to get correct `TemplateDirective` from list in dom
   * @example TemplateDirective.findDirective(QueryList<TemplateDirective>, 'headerTemplate'): `ITemplateRef<T>` { tempRef, tempContext }
   */
  public static findDirective<T>(list: TemplateDirective[] | QueryList<TemplateDirective>, tempName: string): ITemplateRef<T> | null {
    
    const found = list.find(d => d.name == tempName);

    if (!found) return null;

    return { tempRef: found.templateRef, tempContext: found.context };

  }


}
